import { ChartType } from '@dbl/core/chart';

import type {
  IAnalysisDimNumberTrendChartResponseDto,
  IAnalysisDimNumberTrendChartResultVo,
  TAnalysisDimNumberTrendLineDataDto,
  TAnalysisDimNumberTrendLineDataVo,
  TAnalysisDimNumberTrendRankDataDto,
  TAnalysisDimNumberTrendRankDataVo,
} from '../types';
import type { IAnalysisTrendMetricDto } from '../types/trendMetric';

import {
  isMissingWireArray,
  isNonEmptyWireArray,
  normalizeTableDataList,
  pickChartDisplayMeta,
} from './common';

function isEmptyTrendMetric(
  metric: IAnalysisTrendMetricDto | undefined,
): boolean {
  if (metric == null || typeof metric !== 'object') {
    return true;
  }

  return Object.values(metric).every(value => {
    if (value == null || value === '') {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }
    return false;
  });
}

/** DimNumberTrend · `trend[i]` 折线块 wire 是否无有效数据。 */
function isDimNumberTrendLineWireNoData(
  value: TAnalysisDimNumberTrendLineDataDto | null | undefined,
): boolean {
  if (!isNonEmptyWireArray(value)) {
    return true;
  }

  const firstBlock = value[0];
  if (firstBlock == null || typeof firstBlock !== 'object') {
    return true;
  }

  return (
    isMissingWireArray(firstBlock.xAxis) ||
    isMissingWireArray(firstBlock.series)
  );
}

/** DimNumberTrend · `dimension[i]` 排名块 wire 是否无有效数据。 */
function isDimNumberTrendRankWireNoData(
  value: TAnalysisDimNumberTrendRankDataDto | null | undefined,
): boolean {
  if (!isNonEmptyWireArray(value)) {
    return true;
  }

  const firstBlock = value[0];
  if (firstBlock == null || typeof firstBlock !== 'object') {
    return true;
  }

  return isMissingWireArray(firstBlock.series);
}

/** DimNumberTrend · 折线 / 排名 / `trend_metric` 均无有效数据。 */
function isDimNumberTrendWireNoData(params: {
  trend?: TAnalysisDimNumberTrendLineDataDto | null;
  dimension?: TAnalysisDimNumberTrendRankDataDto | null;
  trend_metric?: IAnalysisTrendMetricDto;
}): boolean {
  const { trend, dimension, trend_metric: trendMetric } = params;

  return (
    isDimNumberTrendLineWireNoData(trend) &&
    isDimNumberTrendRankWireNoData(dimension) &&
    isEmptyTrendMetric(trendMetric)
  );
}

/** DimNumberTrend · `trend` 同形透传；`null`/非数组兜底 `[]`。 */
function normalizeDimNumberTrendLineData(
  value: TAnalysisDimNumberTrendLineDataDto | null | undefined,
): TAnalysisDimNumberTrendLineDataVo {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    item => item != null && typeof item === 'object',
  ) as TAnalysisDimNumberTrendLineDataVo;
}

/** DimNumberTrend · `dimension` 同形透传；`null`/非数组兜底 `[]`。 */
function normalizeDimNumberTrendRankData(
  value: TAnalysisDimNumberTrendRankDataDto | null | undefined,
): TAnalysisDimNumberTrendRankDataVo {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    item => item != null && typeof item === 'object',
  ) as TAnalysisDimNumberTrendRankDataVo;
}

export function transformDimNumberTrendChartResponseDtoToVo(
  dto: IAnalysisDimNumberTrendChartResponseDto,
): IAnalysisDimNumberTrendChartResultVo {
  const {
    trend_metric: trendMetric,
    trend: lineDataWire,
    dimension: rankDataWire,
    table_data: tableDataWire,
  } = dto;
  const isNoData = isDimNumberTrendWireNoData({
    trend: lineDataWire,
    dimension: rankDataWire,
    trend_metric: trendMetric,
  });

  return {
    ...pickChartDisplayMeta(dto),
    chartType: ChartType.DimNumberTrend,
    isNoData,
    ...(isNoData
      ? {}
      : {
          trendMetric,
          lineData: normalizeDimNumberTrendLineData(lineDataWire),
          rankData: normalizeDimNumberTrendRankData(rankDataWire),
          tableData: normalizeTableDataList(tableDataWire),
        }),
  };
}
